import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import React from "react";
import PropTypes from "prop-types";
import Link from "../Link";
import { getShopUrl } from "shared-frontend/functions/products";
import { Button, Modal, Title } from "@yoast/ui-library";
import * as styles from "./SubscriptionDetailModalStyles.module.scss";

const messages = defineMessages( {
	paymentSuspended: {
		id: "subscriptionDetail.modal.paymentSuspended",
		defaultMessage: "Unfortunately we were not able to bill your payment method for the next billing cycle. " +
			"This means we have suspend the subscription until the problem is resolved. Sorry for the inconvenience!",
	},
	paymentFailed: {
		id: "subscriptionDetail.header",
		defaultMessage: "Payment Failed",
	},
	manualFix: {
		id: "subscriptionDetail.modal.manualFix",
		defaultMessage: "Whatever the reason may be for the failed payment, you will have to renew your " +
			"subscription manually to fix this. After doing so, the new payment info you provide will be used to " +
			"reactivate your subscription.",
	},
	expiredManualFix: {
		id: "subscriptionDetail.modal.manualFix",
		defaultMessage: "Unfortunately we were not able to bill your payment method for the next billing cycle." +
			" As the failed payment occurred more than a month ago, you will have to buy a new subscription.",
	},
	cancel: {
		id: "subscriptionDetail.modal.cancelButton",
		defaultMessage: "Cancel",
	},
	renew: {
		id: "subscriptionDetail.modal.renewLink",
		defaultMessage: "Renew",
	},
	buy: {
		id: "subscriptionDetail.modal.buyLink",
		defaultMessage: "Buy subscription",
	},
} );

const modalAriaLabel = defineMessages( {
	id: "modal.arialabel.paymentFailed",
	defaultMessage: "Payment failed",
} );

/**
 * Returns the SubscriptionDetailModal.
 *
 * @param {Object} props The props required.
 * @returns { ReactElement } The SubscriptionDetailModal.
 */
const SubscriptionDetailModal = ( props ) => {
	const OneMonthAgo = new Date();
	OneMonthAgo.setMonth( OneMonthAgo.getMonth() - 1 );

	// Only the dates that exist, and only if they are real Dates. `Math.max` over an absent date yields
	// NaN, and every comparison against NaN is false — so a subscription missing either date silently took
	// the "not long expired" branch. A pending-cancel row has no nextPayment at all, so that is the common
	// shape rather than an edge case. The `getTime` check is the cheap guard against a string reaching here:
	// a string has no getTime, which is precisely how this failed before the dates were mapped properly.
	const timestamps = [ props.subscription.nextPayment, props.subscription.endDate ]
		.filter( date => typeof date?.getTime === "function" )
		.map( date => date.getTime() );

	// No usable date means nothing to compare, so keep the gentler copy rather than telling someone their
	// subscription lapsed a month ago on no evidence.
	const expiredMoreThanOneMonthAgo = timestamps.length > 0 &&
		Math.max( ...timestamps ) < OneMonthAgo.getTime();

	const showRenew = ! expiredMoreThanOneMonthAgo && Boolean( props.subscription.renewalUrl );
	const formattedMessage = <FormattedMessage
		id="subscriptionDetail.modal.help"
		defaultMessage="Let us know if you need any help, { supportLink }!"
		values={ {
			supportLink: <Link to="mailto:support@yoast.com">{
				props.intl.formatMessage( {
					id: "subscriptionDetail.support-link",
					defaultMessage: "our support team is available 24/7",
				} )
			}</Link>,
		} }
	/>;

	return (
		<Modal
			isOpen={ props.modalOpen }
			onClose={ props.onClose }
			aria-label={ modalAriaLabel }
		>
			<Modal.Panel>
				<div className={ styles.modalTitle }>
					<Title as="h2" className={ styles.modalTitle }>
						{ props.intl.formatMessage( messages.paymentFailed ) }
					</Title>
				</div>
				{ /* The copy follows the action, not the age. Keying it on `expiredMoreThanOneMonthAgo` alone
					would tell someone to "renew manually" beside a button that can only take them to the shop,
					whenever a recent subscription has no renewal URL. */ }
				<div className={ styles.modalContent }>
					{ showRenew && <p>
						{ props.intl.formatMessage( messages.paymentSuspended ) }
					</p> }
					<p>
						{ showRenew
							? props.intl.formatMessage( messages.manualFix )
							: props.intl.formatMessage( messages.expiredManualFix )
						}
					</p>
					{ showRenew && <p>
						{ formattedMessage }
					</p> }
				</div>
				<div className={ styles.modalFooter }>
					<Button
						onClick={ props.onClose }
						variant="secondary"
					>
						{ props.intl.formatMessage( messages.cancel ) }
					</Button>
					{ /* Buying is the fallback in both senses: when the subscription is too old to renew, and
						when the server did not give us a renewal URL for it. Without the second case this would
						render a "Renew" button with `href={null}` — live-looking and inert. */ }
					<Button
						as="a"
						variant="upsell"
						href={ showRenew ? props.subscription.renewalUrl : getShopUrl() + "/shop" }
					>
						{ showRenew ? props.intl.formatMessage( messages.renew ) : props.intl.formatMessage( messages.buy ) }
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
};


SubscriptionDetailModal.propTypes = {
	onClose: PropTypes.func.isRequired,
	subscription: PropTypes.object.isRequired,
	modalOpen: PropTypes.bool,
	intl: intlShape.isRequired,
};

SubscriptionDetailModal.defaultProps = {
	modalOpen: false,
};

export default injectIntl( SubscriptionDetailModal );
